import type { ValueWithUnit } from '@shared/hydraulic'
import { convertValue } from '@shared/hydraulic/physics/unit-converter'

/**
 * Tidy the two lengths a pipe split produces, without changing the total.
 *
 * A positional split cuts `length` at the exact fraction the pointer landed on, so a 10 ft
 * pipe teed near the middle reports 4.995 / 5.005 — the drop was never going to be exact,
 * and the noise digits read as false precision. Customer ask (PIPE-10): snap to a
 * magnitude-appropriate step while keeping the original overall length.
 *
 * ⚠️ Geometry and elevation are deliberately NOT touched. The device stays exactly where it
 * was dropped; only the reported lengths snap. These are schematics, not scale drawings, so
 * the two were never required to agree — and moving the symbol under the user's cursor to
 * satisfy a rounded number would be far more surprising than the number itself.
 */

/** Ladder bounds: 1 and 5 through every decade. */
const LADDER_MANTISSAS = [1, 5]

/** Guards a boundary total (exactly 10, exactly 50) against float drift tipping a decade. */
const LADDER_EPSILON = 1e-9

/**
 * The rounding increment for a pipe of this total length.
 *
 * Anchored on the two cases the customer specified — 1 ft for pipes up to 10 ft, 5 ft for
 * pipes up to 50 ft — which generalise to: take the smallest 1-5 ladder bound at or above
 * the total, and divide by ten. So (10, 50] rounds on 5, (50, 100] on 10, and so on in both
 * directions.
 */
export function stepForTotal(total: number): number {
  if (!Number.isFinite(total) || total <= 0) return 0
  const scaled = total * (1 - LADDER_EPSILON)
  const decade = Math.floor(Math.log10(scaled))
  // Walk this decade then the next; the ladder is sparse enough that two is always enough.
  for (const exp of [decade, decade + 1]) {
    for (const mantissa of LADDER_MANTISSAS) {
      const bound = mantissa * 10 ** exp
      if (scaled <= bound) return bound / 10
    }
  }
  return 10 ** decade
}

/**
 * Split `total` at fraction `t`, rounded to a clean step in `displayUnit`.
 *
 * Returns `null` when rounding cannot help or would do harm — an absent/zero/negative
 * length, a non-finite fraction, or a pipe too short to hold two whole steps — in which
 * case the caller keeps the exact proportional cut.
 *
 * ⚠️ Rounds in the unit the USER READS, not the unit the model stores. Every pipe length in
 * every real model is persisted in metres while US users work in feet, so rounding the
 * stored number would snap to the nearest metre and surface as 9.84 ft in the panel.
 */
export function roundSplitLengths(
  total: ValueWithUnit | undefined,
  t: number,
  displayUnit: string,
): { a: ValueWithUnit; b: ValueWithUnit } | null {
  if (!total || !Number.isFinite(total.value) || total.value <= 0) return null
  if (!Number.isFinite(t)) return null

  const totalDisplay = convertValue(total.value, total.unit, displayUnit)
  if (!Number.isFinite(totalDisplay) || totalDisplay <= 0) return null

  const step = stepForTotal(totalDisplay)
  // Below two steps there is no interior multiple to snap to: rounding could only yield a
  // zero-length half or swallow the whole pipe. Leave the exact cut alone.
  if (step <= 0 || totalDisplay < step * 2) return null

  // ⚠️ Clamp to one FULL step at each end. A device dropped 0.4 ft along a 10 ft pipe rounds
  // to zero, and a zero-length pipe is singular in the solver — the same failure
  // `clampSplitT` / `clampSplitGeometry` guard against on the geometry side.
  const snapped = Math.round((totalDisplay * t) / step) * step
  const aDisplay = Math.min(Math.max(snapped, step), totalDisplay - step)

  // ⚠️ Derive B by SUBTRACTION, never by rounding it independently. Two independent rounds
  // drift apart and the split silently gains or loses pipe — the customer explicitly asked
  // for the overall length to survive.
  return {
    a: { value: convertValue(aDisplay, displayUnit, total.unit), unit: total.unit },
    b: { value: total.value - convertValue(aDisplay, displayUnit, total.unit), unit: total.unit },
  }
}
